import React, { useState, useRef, useEffect } from 'react';
// Google Maps loader (simple script loader)
function loadGoogleMapsScript(apiKey, callback) {
  if (window.google && window.google.maps) {
    callback();
    return;
  }
  const existing = document.getElementById('google-maps-script');
  if (existing) {
    existing.onload = callback;
    return;
  }
  const script = document.createElement('script');
  script.id = 'google-maps-script';
  script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}`;
  script.async = true;
  script.onload = callback;
  document.body.appendChild(script);
}
import { submitRestaurant, fetchAPI } from 'shared/api';
import { validateRestaurant } from 'shared/validators';


const RestaurantForm = ({ token, initialData, isEdit, onSuccess, onCancel }) => {
  const [formData, setFormData] = useState(() => {
    if (initialData) {
      // Support both flat and nested details structure
      const details = initialData.details || {};
      return {
        restaurant_name: initialData.restaurant_name || initialData.name || '',
        address: initialData.address || '',
        contact_number: details.contact_number || initialData.contact_number || '',
        is_whatsapp_enabled: details.is_whatsapp_enabled ?? initialData.is_whatsapp_enabled ?? false,
        whatsapp_message_consent: details.whatsapp_message_consent ?? initialData.whatsapp_message_consent ?? false,
        home_delivery_supported: details.home_delivery_supported ?? initialData.home_delivery_supported ?? false,
        radius_of_service: details.radius_of_service ?? initialData.radius_of_service ?? '',
        minimum_order_amount: details.minimum_order_amount ?? initialData.minimum_order_amount ?? '',
        cuisine: details.cuisine ?? initialData.cuisine ?? '',
        vegetarian_type: details.vegetarian_type ?? initialData.vegetarian_type ?? '',
        geolocation: initialData.geolocation || { latitude: '', longitude: '' },
        userrole: initialData.userrole || initialData.user_role || initialData.user_type || 'ROLE_restaurant_manager',
      };
    } else {
      return {
        restaurant_name: '',
        address: '',
        contact_number: '',
        is_whatsapp_enabled: false,
        whatsapp_message_consent: false,
        home_delivery_supported: false,
        radius_of_service: '',
        minimum_order_amount: '',
        cuisine: '',
        vegetarian_type: '',
        geolocation: { latitude: '', longitude: '' },
        userrole: 'ROLE_restaurant_manager',
      };
    }
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  // Google Maps API key (replace with your key or use env)
  const GOOGLE_MAPS_API_KEY = 'AIzaSyD34DbLoPDDiq251QmlRI_7tLZKGn721wo';

  // Load map when form is shown (edit or create)
  useEffect(() => {
    if (!mapRef.current) return;
    loadGoogleMapsScript(GOOGLE_MAPS_API_KEY, () => {
      setMapLoaded(true);
    });
  }, []);

  useEffect(() => {
    if (!mapLoaded || !mapRef.current) return;
    // Default to India center if not set
    const lat = parseFloat(formData.geolocation.latitude) || 20.5937;
    const lng = parseFloat(formData.geolocation.longitude) || 78.9629;
    const map = new window.google.maps.Map(mapRef.current, {
      center: { lat, lng },
      zoom: 5,
    });
    // Place marker if lat/lng present
    let marker = new window.google.maps.Marker({
      position: { lat, lng },
      map,
      draggable: true,
    });
    markerRef.current = marker;
    // Update formData on marker drag
    marker.addListener('dragend', function (e) {
      setFormData(f => ({
        ...f,
        geolocation: {
          latitude: e.latLng.lat(),
          longitude: e.latLng.lng(),
        },
      }));
    });
    // Update marker on map click
    map.addListener('click', function (e) {
      marker.setPosition(e.latLng);
      setFormData(f => ({
        ...f,
        geolocation: {
          latitude: e.latLng.lat(),
          longitude: e.latLng.lng(),
        },
      }));
    });
    // Update marker if formData changes
    return () => {
      marker.setMap(null);
    };
  }, [mapLoaded]);

  // Keep marker in sync with formData
  useEffect(() => {
    if (!markerRef.current) return;
    const lat = parseFloat(formData.geolocation.latitude);
    const lng = parseFloat(formData.geolocation.longitude);
    if (!isNaN(lat) && !isNaN(lng)) {
      markerRef.current.setPosition({ lat, lng });
      markerRef.current.getMap().setCenter({ lat, lng });
    }
  }, [formData.geolocation.latitude, formData.geolocation.longitude]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errors = validateRestaurant(formData);
    if (errors.length > 0) {
      setError(errors.join(', '));
      return;
    }
    try {
      if (isEdit && initialData && initialData.id) {
        // PATCH update
        formData.userrole = formData.userrole || 'ROLE_restaurant_manager'; // Ensure user role is set
        const response = await fetchAPI(`/restaurant/${initialData.id}`, {
          method: 'PUT',
          headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(formData),
        });
        if (!response.ok) throw new Error('Failed to update restaurant');
        setSuccess('Restaurant updated successfully!');
        setError('');
        if (onSuccess) onSuccess();
      } else {
        const result = await submitRestaurant(token, formData);
        setSuccess(`Restaurant created: ${result.restaurant_staging_id}`);
        setError('');
        if (onSuccess) onSuccess();
      }
    } catch (err) {
      setError(err.message);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.includes('geolocation')) {
      const key = name.split('.')[1];
      setFormData({ ...formData, geolocation: { ...formData.geolocation, [key]: value } });
    } else {
      setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
    }
  };

  return (
    <div className="max-w-md mx-auto p-4 bg-white shadow-md rounded">
      <h2 className="text-2xl font-bold mb-4">Restaurant Details</h2>
      <form onSubmit={handleSubmit}>
        <div className="mb-4">
          <label className="block text-sm font-medium">Name</label>
          <input
            type="text"
            name="restaurant_name"
            value={formData.restaurant_name}
            onChange={handleChange}
            className="w-full p-2 border rounded"
            required
            disabled={isEdit && !onCancel}
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium">Address</label>
          <textarea
            name="address"
            value={formData.address}
            onChange={handleChange}
            className="w-full p-2 border rounded"
            required
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium">Contact Number</label>
          <input
            type="text"
            name="contact_number"
            value={formData.contact_number}
            onChange={handleChange}
            className="w-full p-2 border rounded"
            required
          />
        </div>
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              name="is_whatsapp_enabled"
              checked={formData.is_whatsapp_enabled}
              onChange={handleChange}
              className="mr-2"
            />
            WhatsApp Enabled
          </label>
        </div>
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              name="whatsapp_message_consent"
              checked={formData.whatsapp_message_consent}
              onChange={handleChange}
              className="mr-2"
            />
            WhatsApp Consent
          </label>
        </div>
        <div className="mb-4">
          <label className="flex items-center">
            <input
              type="checkbox"
              name="home_delivery_supported"
              checked={formData.home_delivery_supported}
              onChange={handleChange}
              className="mr-2"
            />
            Home Delivery
          </label>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium">Radius of Service (km)</label>
          <input
            type="number"
            name="radius_of_service"
            value={formData.radius_of_service}
            onChange={handleChange}
            className="w-full p-2 border rounded"
            step="0.1"
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium">Minimum Order Amount</label>
          <input
            type="number"
            name="minimum_order_amount"
            value={formData.minimum_order_amount}
            onChange={handleChange}
            className="w-full p-2 border rounded"
            step="0.01"
          />
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium">Restaurant Location (select on map)</label>
          <div style={{ width: '100%', height: 300, borderRadius: 8, overflow: 'hidden', marginBottom: 8 }}>
            <div ref={mapRef} style={{ width: '100%', height: '100%' }} />
          </div>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="block text-xs">Latitude</label>
              <input
                type="number"
                name="geolocation.latitude"
                value={formData.geolocation.latitude}
                onChange={handleChange}
                className="w-full p-2 border rounded"
                step="0.0001"
              />
            </div>
            <div className="flex-1">
              <label className="block text-xs">Longitude</label>
              <input
                type="number"
                name="geolocation.longitude"
                value={formData.geolocation.longitude}
                onChange={handleChange}
                className="w-full p-2 border rounded"
                step="0.0001"
              />
            </div>
          </div>
          <div className="text-xs text-gray-500 mt-1">Drag the marker or click on the map to set your restaurant's location.</div>
        </div>
        {error && <p className="text-red-500">{error}</p>}
        {success && <p className="text-green-500">{success}</p>}
        <div style={{ display: 'flex', gap: 8 }}>
          <button type="submit" className="w-full bg-blue-500 text-white p-2 rounded hover:bg-blue-600">
            {isEdit ? 'Update Restaurant' : 'Submit Restaurant'}
          </button>
          {isEdit && onCancel && (
            <button type="button" className="w-full bg-gray-400 text-white p-2 rounded hover:bg-gray-500" onClick={onCancel}>
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
};

export default RestaurantForm;